import { useState } from 'react';

interface AddFileDialogProps {
  onClose: () => void;
  onCreate: (relPath: string) => Promise<void>;
}

const KIND_OPTIONS = [
  { label: 'reference (markdown)', prefix: 'references/', ext: '.md' },
  { label: 'script (shell)', prefix: 'scripts/', ext: '.sh' },
  { label: 'eval cases (yaml)', prefix: 'evals/', ext: '.yaml' },
] as const;

export function AddFileDialog({ onClose, onCreate }: AddFileDialogProps) {
  const [kindIdx, setKindIdx] = useState(0);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kind = KIND_OPTIONS[kindIdx];
  const cleanName = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const fullPath = cleanName ? `${kind.prefix}${cleanName}${kind.ext}` : '';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!cleanName) {
      setError('Pick a filename.');
      return;
    }
    setBusy(true);
    try {
      await onCreate(fullPath);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Add file failed');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-lg border bg-canvas p-5 text-sm"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,.18)' }}
      >
        <h2 className="text-base font-semibold">Add a file to this skill</h2>
        <p className="mt-1 text-[12px] text-text-subtle">
          Files live alongside SKILL.md and get rendered into{' '}
          <code className="font-mono">~/.claude/skills/&lt;slug&gt;/</code> by the CLI.
        </p>

        <label className="mt-4 block text-[10px] uppercase tracking-wider text-text-subtle">
          Kind
          <select
            value={kindIdx}
            onChange={(e) => setKindIdx(Number(e.target.value))}
            className="mt-1 block w-full rounded border bg-surface px-2 py-1 text-[13px] outline-none focus:border-[var(--color-accent)]"
          >
            {KIND_OPTIONS.map((k, i) => (
              <option key={i} value={i}>
                {k.label}
              </option>
            ))}
          </select>
        </label>

        <label className="mt-3 block text-[10px] uppercase tracking-wider text-text-subtle">
          Filename
          <div className="mt-1 flex items-center rounded border bg-surface px-2 py-1 font-mono text-[12px]">
            <span className="text-text-subtle">{kind.prefix}</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={kindIdx === 0 ? 'api-errors' : kindIdx === 1 ? 'setup' : 'cases'}
              autoFocus
              className="flex-1 bg-transparent outline-none"
            />
            <span className="text-text-subtle">{kind.ext}</span>
          </div>
        </label>

        {fullPath && (
          <p className="mt-2 text-[11px] text-text-subtle">
            Will create <code className="font-mono">{fullPath}</code>
          </p>
        )}
        {error && (
          <p className="mt-3 rounded border bg-danger-soft p-2 text-[11px] text-[var(--color-danger)]">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn">
            Cancel
          </button>
          <button type="submit" disabled={busy || !cleanName} className="btn btn-primary">
            {busy ? 'Adding…' : 'Add file'}
          </button>
        </div>
      </form>
    </div>
  );
}
